import z from 'zod';

import { InstitutionType } from '@do/models';
import type { PageSeoData } from '@do/svelte-seo';

import { seoConfigs } from '@entities/seo';

const galleryAgencyPayloadSchema = z.object({
  entityName: z.string(),
  artistName: z.string(),
  artworkName: z.string(),
});

const configSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(InstitutionType.Agency),
    payload: galleryAgencyPayloadSchema,
  }),
  z.object({
    type: z.literal(InstitutionType.Gallery),
    payload: galleryAgencyPayloadSchema,
  }),
  z.object({
    type: z.literal(InstitutionType.Artist),
    payload: z.object({
      entityName: z.string(),
      artworkName: z.string(),
    }),
  }),
  z.object({
    type: z.literal(InstitutionType.Photographer),
    payload: z.object({
      entityName: z.string(),
      artworkName: z.string(),
    }),
  }),
  z.object({
    type: z.literal(InstitutionType.Collector),
    payload: z.object({
      entityName: z.string(),
      artistName: z.string(),
      artworkName: z.string(),
    }),
  }),
  z.object({
    type: z.literal(InstitutionType.Curator),
    payload: z.object({
      entityName: z.string(),
      artistName: z.string(),
      artworkName: z.string(),
    }),
  }),
]);

type Params = z.infer<typeof configSchema>;

export class ArtworkSeoData extends seoConfigs.SeoDataBuilder<Params> {
  constructor(type: InstitutionType, payload: Record<string, any>, commonData: PageSeoData) {
    const fallbackData = {
      title: 'Artwork Page',
      metaDescription: `Explore the vibrant art of available for purchase in the Digital Storefront. Discover unique digital originals and convert art into digital assets.`,
      breadcrumbs: [],
    };

    super({ type, payload }, configSchema, fallbackData, commonData);
  }

  protected createPageSeoData({ type, payload }: Params): Partial<PageSeoData> {
    switch (type) {
      case InstitutionType.Agency:
      case InstitutionType.Gallery:
        return this.getGalleryAgencySeo(payload.artworkName, payload.artistName, payload.entityName);
      case InstitutionType.Artist:
        return this.getArtistSeo(payload.artworkName, payload.entityName);
      case InstitutionType.Curator:
        return this.getCuratorSeo(payload.artworkName, payload.artistName, payload.entityName);
      case InstitutionType.Collector:
        return this.getCollectorSeo(payload.artworkName, payload.artistName, payload.entityName);
      default:
        return {};
    }
  }

  private getGalleryAgencySeo(artworkName: string, artistName: string, entityName: string): Partial<PageSeoData> {
    return {
      title: `${artworkName} by ${artistName} from ${entityName} is Available in the Digital Original Store.`,
      metaDescription: `✓ Acquire ${artworkName} by ${artistName} ✓ Explore and purchase unique art by ${artistName} from ${entityName} ✓ Immerse yourself in digital art with Digital Original`,
    };
  }

  private getArtistSeo(artworkName: string, entityName: string): Partial<PageSeoData> {
    return {
      title: `${artworkName} by ${entityName} is Available in the Digital Original Store.`,
      metaDescription: `✓ Acquire ${artworkName} by ${entityName} ✓ Explore and purchase unique art by ${entityName} ✓ Immerse yourself in digital art with Digital Original`,
    };
  }

  private getCuratorSeo(artworkName: string, artistName: string, entityName: string): Partial<PageSeoData> {
    return {
      title: `${artworkName} by ${artistName} – Curated by ${entityName} and Available for Purchase in Digital Format | Digital Original`,
      metaDescription: `✓ Acquire ${artworkName} by ${artistName}, available exclusively in the Digital Original Store. ✓ Explore and purchase unique digital art pieces, and immerse yourself in the world of digital art through curated collections.`,
    };
  }

  private getCollectorSeo(artworkName: string, artistName: string, entityName: string): Partial<PageSeoData> {
    return {
      title: `${artworkName} by ${artistName} – Presented by ${entityName} and Available for Purchase in Digital Format | Digital Original`,
      metaDescription: `✓ Acquire ${artworkName} by ${artistName}, available exclusively in the Digital Original Store. ✓ Explore and purchase unique digital art pieces, and immerse yourself in the world of digital art through curated collections.`,
    };
  }
}
